import * as React from 'react';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { AmorgAdminToken, AmorgAdminUserListing } from "../../amorg-common";
import { getActiveUser, getUsers } from '../../api/adminClientApi';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { Table } from '../../designSystem/Table';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { Button } from '../../designSystem/button/Button';
import { ButtonRow } from '../../designSystem/button/ButtonRow';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { AmorgAdminPageProvider } from './AmorgAdminPageContext';
import { EditUserModal } from './user/_components/EditUserModal';
import { CreateUserModal } from './user/_components/CreateUserModal';
import { AdminTab } from './_components/AdminTab';

export default function AmorgAdminUsers (): JSX.Element 
{
    const history = useHistory();

    const [activeUser, setActiveUser] = React.useState<AmorgAdminToken>();
    const [loading, setLoading] = React.useState(true);
    const [users, setUsers] = React.useState<AmorgAdminUserListing[]>([]);
    const [showCreateUser, setShowCreateUser] = React.useState(false);
    const [editUser, setEditUser] = React.useState<AmorgAdminUserListing | null>(null);

    const getSortedUsers = async () =>
    {
        const users = await getUsers(() => history.push(`/amorgy/login`));
        users.sort((lhs, rhs) => lhs.email.localeCompare(rhs.email));
        return users;
    };

    async function refreshUsers ()
    {
        setLoading(true);
        const users = await getSortedUsers();
        setUsers(users);
        setLoading(false);

        return users;
    }

    useEffect(() =>
    {
        const init = async () =>
        {
            setActiveUser(await getActiveUser(() => history.push(`/amorgy/login`)));
            await refreshUsers();
        };
        init().catch(console.error);
    }, []);

    async function handleUserCreated ()
    {
        setShowCreateUser(false);
        refreshUsers();
    }

    async function handleUserUpdated ()
    {
        const editUserId = users.find(u => u.userId === editUser?.userId).userId;
        
        if(!editUserId){
            console.log('Unable to find edit user');
            return;
        }

        const updatedUsers = await refreshUsers();
        const userInEdit = updatedUsers.find(u => u.userId === editUserId);
        if(!userInEdit){
            console.log('Unable to reload user in edit');
            return;
        }
        setEditUser(userInEdit);
    }

    async function handleCloseEdit ()
    {
        setEditUser(null);
    }

    return (
        <AmorgAdminPageProvider>
            <LoadingPageOverlay loading={loading} />

            <SingleColumnLayout
                noDivider
                headerContainer={
                    <Flex flexDirection="column" gap={16}>
                        <AdminTab selectedTab='users' />
                        {activeUser && activeUser.superAdmin &&
                            <ButtonRow>
                                <Button variant="filled" icon="actions_plus" text="Opret ny bruger" onClick={() => setShowCreateUser(true)} />
                            </ButtonRow>
                        }
                    </Flex>
                }

                contentContainer={
                    <Flex flexDirection='column' gap={16}>
                        <Table>
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Partner</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map(u =>
                                    <tr key={u.userId}>
                                        <td style={{ textDecoration: u.enabled ? 'none' : 'line-through' }}>
                                            {u.email}
                                        </td>
                                        <td>
                                            {u.partner ?? ''}
                                        </td>
                                        <td>
                                            {activeUser && activeUser.superAdmin &&
                                                <Flex justifyContent="flex-end">
                                                    <Button variant='filled' text="Rediger" icon="navigation_settings" onClick={() => setEditUser(users.find(u2 => u2.userId === u.userId))} />
                                                </Flex>

                                            }
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        <CreateUserModal
                            show={showCreateUser}
                            onCreated={handleUserCreated}
                            onCancel={() => setShowCreateUser(false)}
                        />
                        <EditUserModal
                            show={editUser !== null}
                            user={
                                editUser
                                    ? { id: editUser.userId, email: editUser.email, enabled: editUser.enabled, partner: editUser.partner }
                                    : null
                            }
                            onClose={() => handleCloseEdit()}
                            onUpdated={handleUserUpdated}
                        />

                    </Flex>
                } />
        </AmorgAdminPageProvider>
    );
}